import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Plus, RefreshCw, MoreHorizontal, Trash2, Pencil,
  TrendingUp, TrendingDown, Minus,
} from 'lucide-react';

export function KeywordFormDialog({
  open, onOpenChange, editingKeyword, formData, setFormData,
  clients, onSubmit, onReset,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) onReset(); }}>
      <DialogTrigger asChild>
        <Button className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="add-keyword-btn">
          <Plus className="w-4 h-4 mr-2" /> Ajouter un mot-cle
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {editingKeyword ? 'Modifier le mot-cle' : 'Nouveau mot-cle'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="keyword">Mot-cle *</Label>
            <Input id="keyword" value={formData.keyword}
              onChange={(e) => setFormData({ ...formData, keyword: e.target.value })}
              placeholder="Ex: agence seo paris" required className="mt-1" data-testid="keyword-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="client">Client *</Label>
              <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })} required>
                <SelectTrigger className="mt-1" data-testid="keyword-client-select"><SelectValue placeholder="Selectionner..." /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="priority">Priorite</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                <SelectTrigger className="mt-1" data-testid="keyword-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">Haute</SelectItem>
                  <SelectItem value="medium">Moyenne</SelectItem>
                  <SelectItem value="low">Basse</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="target_url">URL cible</Label>
            <Input id="target_url" value={formData.target_url}
              onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
              placeholder="https://example.com/page" className="mt-1" data-testid="keyword-url-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Localisation</Label>
              <Select value={formData.location} onValueChange={(v) => setFormData({ ...formData, location: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="France">France</SelectItem>
                  <SelectItem value="Belgium">Belgique</SelectItem>
                  <SelectItem value="Switzerland">Suisse</SelectItem>
                  <SelectItem value="Canada">Canada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Langue</Label>
              <Select value={formData.language} onValueChange={(v) => setFormData({ ...formData, language: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="French">Francais</SelectItem>
                  <SelectItem value="English">Anglais</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => { onOpenChange(false); onReset(); }}>Annuler</Button>
            <Button type="submit" className="bg-[#002FA7] hover:bg-[#002277]" data-testid="save-keyword-btn">
              {editingKeyword ? 'Enregistrer' : 'Creer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const PRIORITY_COLORS = {
  high: 'bg-red-50 text-red-700',
  medium: 'bg-yellow-50 text-yellow-700',
  low: 'bg-neutral-100 text-neutral-600',
};
const PRIORITY_LABELS = { high: 'Haute', medium: 'Moyenne', low: 'Basse' };

export function KeywordRow({ kw, clientName, trackingKeyword, onTrack, onEdit, onDelete }) {
  const change = (kw.current_position !== null && kw.previous_position !== null)
    ? kw.previous_position - kw.current_position
    : null;

  const positionBadge = change === null
    ? <span className="rank-stable">-</span>
    : change > 0
      ? <span className="rank-up flex items-center gap-1"><TrendingUp className="w-3 h-3" />+{change}</span>
      : change < 0
        ? <span className="rank-down flex items-center gap-1"><TrendingDown className="w-3 h-3" />{change}</span>
        : <span className="rank-stable flex items-center gap-1"><Minus className="w-3 h-3" />0</span>;

  return (
    <tr>
      <td>
        <span className="font-medium text-neutral-900">{kw.keyword}</span>
        {kw.target_url && (
          <span className="block text-xs text-neutral-400 truncate max-w-[200px]">{kw.target_url}</span>
        )}
      </td>
      <td className="text-neutral-600">{clientName}</td>
      <td className="text-right">
        <span className="font-mono text-lg font-bold text-neutral-900">
          {kw.current_position !== null ? kw.current_position : '-'}
        </span>
      </td>
      <td className="text-right">{positionBadge}</td>
      <td className="text-right">
        <span className="font-mono text-neutral-700">
          {kw.search_volume != null && kw.search_volume > 0
            ? kw.search_volume.toLocaleString('fr-FR')
            : <span className="text-neutral-300">-</span>}
        </span>
      </td>
      <td>
        <span className={`text-xs font-mono px-2 py-1 rounded ${PRIORITY_COLORS[kw.priority]}`}>
          {PRIORITY_LABELS[kw.priority] || kw.priority}
        </span>
      </td>
      <td className="text-neutral-500 text-sm">
        {kw.last_checked ? new Date(kw.last_checked).toLocaleDateString('fr-FR') : 'Jamais'}
      </td>
      <td>
        <div className="flex items-center gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={() => onTrack(kw.id)}
            disabled={trackingKeyword === kw.id} data-testid={`track-keyword-${kw.id}`}>
            <RefreshCw className={`w-4 h-4 ${trackingKeyword === kw.id ? 'animate-spin' : ''}`} />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid={`keyword-menu-${kw.id}`}>
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(kw)} data-testid={`edit-keyword-${kw.id}`}>
                <Pencil className="w-4 h-4 mr-2" /> Modifier
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete(kw.id)} className="text-red-600">
                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
}
